import React from 'react';
import styled, { keyframes } from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import Gap from '@component/common/gap';
import Placeholder from '@component/common/placeholder';
import Canvas from '@design-system/canvas';
import GrammarChoice from '@component/iyagigo/cloze/study/grammar-choice';
import { TWorksheetStudyContent } from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';
import { completeSentence } from '@util/string';
import { GRAMMAR_CHIP_HEIGHT_JP, GRAMMAR_CHIP_PADDING } from '@constant/design';
import * as color from '@constant/color';
import * as size from '@constant/size';
import Line from '@component/common/line';

interface IProps {
  title: string;
  description: string;
  source: TWorksheetStudyContent;
  sentence: string;
  answered: boolean;
  correct: boolean;
  handleCheck: () => void;
  handleRetry: () => void;
  selected: { id: string; word: string; idx: number }[];
  handleSelect: (word: string, idx: number) => void;
  handleRemove: (id: string) => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;
const AnswerContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  width: 100%;
  min-height: 120px;
`;
const SelectedItemsContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const pointerFloat = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
`;
const AnswerControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
const AnswerButtonContainer = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 5px;
  height: ${size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};
`;
const PointerContainer = styled.span<{ $down: boolean }>`
  display: flex;
  rotate: ${({ $down }) => ($down ? '180deg' : '0deg')};
  animation: ${pointerFloat} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ClozeContent: React.FC<IProps> = ({
  title,
  description,
  source,
  sentence,
  answered,
  correct,
  selected,
  handleSelect,
  handleRemove,
  handleCheck,
  handleRetry,
}) => {
  const isEnglish = source.languageCode === 'EN';
  const choices = isEnglish ? source.choiceEn : source.choiceJa;
  const answers = isEnglish ? source.answerEn : source.answerJa;
  const pronunciation = isEnglish ? '' : source.sentencePronunciationJa;
  const hasSelection = selected.length > 0;
  const resultColor = correct ? color.GREEN_3 : color.RED_2;
  const choicePronunciations = (isEnglish ? [] : source.choicePronunciationJa) ?? [];
  const selectedBorderColor = answered ? resultColor : color.YELLOW_2;
  const SelectedItems = selected.map((item) => {
    const choicePronunciation = choicePronunciations[choices.indexOf(item.word)];
    return (
      <TextBlock
        key={item.id}
        text={item.word}
        annotation={choicePronunciation ? { reading: choicePronunciation } : undefined}
        reserveAnnotationSpace={!!pronunciation}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: true },
          box: {
            alignX: 'center',
            padding: GRAMMAR_CHIP_PADDING,
            height: GRAMMAR_CHIP_HEIGHT_JP,
            backgroundColor: color.WHITE_1,
            border: true,
            borderColor: selectedBorderColor,
            borderRadius: size.BORDER_RADIUS_1,
            borderWidth: size.BORDER_WIDTH_1,
            event: { cursor: answered ? 'auto' : 'pointer' },
          },
        }}
        click={answered ? undefined : { handler: () => handleRemove(item.id) }}
      />
    );
  });

  return (
    <Wrapper>
      <TextBlock
        text={title}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <TextBlock
        text={description}
        design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_2} />
      <Canvas theme={ETheme.PAPER} shadow width='100%' padding={size.CONTENT_PADDING}>
        <TextBlock
          text={completeSentence(source.sentenceTemplateKo, source.answerKo, false) || source.sentenceKo}
          design={{ text: { size: 'medium', color: color.BROWN_1, bold: true }, box: { alignX: 'left' } }}
        />
        <Line direction='horizontal' width='100%' color={color.GRAY_1} margin={`10px 0`} />
        {/*<Gap height={size.MARGIN_2} />*/}
        <AnswerContainer>
          <Placeholder
            visible={answered}
            alignY='top'
            placeholder={hasSelection ? <SelectedItemsContainer>{SelectedItems}</SelectedItemsContainer> : null}>
            <TextBlock
              text={sentence}
              annotation={pronunciation ? { reading: pronunciation } : undefined}
              design={{
                text: { size: 'large', color: color.BLACK_2, bold: true },
                box: { alignX: 'left', width: '100%' },
              }}
            />
          </Placeholder>
        </AnswerContainer>
        <Gap height={size.MARGIN_1} />
        <AnswerControlContainer>
          <AnswerButtonContainer
            $clickable={answered || hasSelection}
            onClick={answered ? handleRetry : hasSelection ? handleCheck : undefined}>
            {answered ? (
              <IconBlock icon='repeat' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
            ) : (
              <PointerContainer $down={!hasSelection}>
                <IconBlock icon='touch-app' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
              </PointerContainer>
            )}
            <TextBlock
              text={answered ? '다시 풀기' : hasSelection ? '정답 보기' : '보기 고르기'}
              design={{ text: { size: 'medium', color: color.GRAY_1 } }}
            />
          </AnswerButtonContainer>
        </AnswerControlContainer>
      </Canvas>
      {answered && (
        <>
          <Gap height={size.MARGIN_2} />
          <IconTextBlock
            icon={correct ? 'circle' : 'close'}
            arrangement='text-icon'
            text={correct ? '정답이에요' : '오답이에요'}
            design={{
              icon: { size: 'tiny', color: resultColor },
              text: { size: 'medium', color: resultColor, bold: true },
              box: { alignX: 'left', gap: '5px' },
            }}
          />
          <Gap height={size.MARGIN_1} />
          <TextBlock
            text='만든 문장'
            design={{ text: { size: 'small', color: resultColor, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height='5px' />
          <SelectedItemsContainer>{SelectedItems}</SelectedItemsContainer>
        </>
      )}
      {!answered && (
        <>
          <Gap height={size.MARGIN_2} />
          <GrammarChoice
            workbookKey={source.key}
            choice={choices}
            choicePronunciation={choicePronunciations}
            answer={answers}
            sentence={sentence}
            isAnswerView={false}
            selectedWords={selected}
            handleWordSelect={handleSelect}
          />
        </>
      )}
    </Wrapper>
  );
};

export default ClozeContent;
